import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { addContact } from 'actions/ringCentral';
import { TASK_TYPES } from 'features/daily-plan/constants';
import { PhoneNumbersMenu } from 'features/message-center/components/SearchContactsDrawer/components';
import { useChatContext } from 'features/message-center/providers';
import { THEME } from 'GlobalStyles';
import strings from 'strings';
import { FPIcon, FPIconButton } from 'UI/components/atoms';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import FPLink from 'UI/components/atoms/FPLink';
import { When } from 'UI/components/atoms/When';
import FPButtonMenu from 'UI/components/molecules/FPButtonMenu';
import { DEFAULT_COUNTRY_CODE } from 'UI/constants/defaults';
import { ENTITY_TYPE_ID_MAP, EntityType } from 'UI/constants/entityTypes';
import { SvgEmailFilled } from 'UI/res';

import { TASK_ICON_MAP } from './TaskDetails.constants';
import {
  ActionsContainer,
  CommunicationActionsContainer,
  Content,
  EmailAction,
  ReachOutToContainer,
  TaskTitleLabel,
  TitleContainer
} from './TaskDetails.styled';
import { buildCompanyContactData, getLinkProps, getRelatedItem } from './TaskDetails.utils';

const { taskDetails: COPIES } = strings.dailyPlan.drawer;

const ICON_COLOR = THEME.palette.grey[600];
const DISABLED_COLOR = THEME.palette.grey[300];

export const TaskDetails = ({
  candidate,
  date,
  entity,
  entityType,
  jobOrder,
  sendout,
  status,
  taskType,
  customTask = null,
  onEditTask = () => {},
  onOpenChangeLogs = () => {}
}) => {
  const dispatch = useDispatch();
  const { openDrawer: openChatDrawer } = useChatContext();
  const relatedItem = useMemo(
    () => getRelatedItem(taskType?.id, { candidate, jobOrder, sendout }),
    [candidate, jobOrder, sendout, taskType]
  );

  const isCompany = entityType?.id === ENTITY_TYPE_ID_MAP.company;

  const contactData = isCompany ? buildCompanyContactData(entity) : entity;

  const Icon = TASK_ICON_MAP[status?.id] ?? null;
  const menuItems = [
    {
      title: COPIES.changeLog.title,
      action: onOpenChangeLogs,
      visible: true
    }
  ];

  const handlePhoneClick = ({ contact: receivedContact, selectedPhone }) => {
    if (!receivedContact || !selectedPhone) return;

    dispatch(
      addContact({
        id: receivedContact.id,
        role: entityType.code,
        phone: selectedPhone.phone,
        createdAt: new Date()
      })
    );
  };

  const handleOnTextClick = ({ contact: receivedContact, selectedPhone }) => {
    if (!receivedContact || !selectedPhone || !entityType.code) return;
    const recipient = {
      countryCode: selectedPhone.country_code ?? DEFAULT_COUNTRY_CODE,
      entityId: receivedContact.id || null,
      entityType: entityType.code,
      name: receivedContact.fullName || receivedContact.name || '',
      phone: selectedPhone.phone
    };

    openChatDrawer({
      recipient
    });
  };

  return (
    <Grid container>
      <TitleContainer container item xs={12}>
        <Grid item sm={8}>
          <TaskTitleLabel variant="subtitle1">
            <b>{taskType?.title}</b>
          </TaskTitleLabel>
          <When condition={!!customTask}>
            <Typography>{customTask}</Typography>
          </When>
          {taskType?.id !== TASK_TYPES.other && relatedItem && (
            <FPLink linkProps={getLinkProps(relatedItem.id, relatedItem.entityType)}>
              {relatedItem.title}
            </FPLink>
          )}
        </Grid>

        <ActionsContainer item sm={4}>
          <FPActionButton
            onClick={onEditTask}
            size="medium"
            text={strings.shared.ui.edit}
            variant="outlined"
          />

          <FPButtonMenu isIconButton menuItems={menuItems} />
        </ActionsContainer>
      </TitleContainer>

      <Content item xs={12}>
        <Typography color="textSecondary" variant="body2">
          <b>{date}</b>
        </Typography>

        <When condition={!!entity?.id}>
          <Typography color="textSecondary" variant="body1">
            <Typography variant="overline">{COPIES.reachOutTo}</Typography>
            <FPLink linkProps={getLinkProps(entity?.id, entityType?.code)}>{entity?.name}</FPLink>
            &nbsp;·&nbsp;{entityType?.title}
          </Typography>
          <ReachOutToContainer>
            {entity?.title && entity?.company?.name && (
              <Typography color="textSecondary" variant="body1">
                {entity.title} &nbsp;·&nbsp;
                <FPLink linkProps={getLinkProps(entity?.company?.id, EntityType.Company)}>
                  {entity?.company?.name}
                </FPLink>
              </Typography>
            )}
            {entityType?.code === EntityType.Company && (
              <Typography variant="body1">
                {entity?.type}&nbsp;·&nbsp;{entity?.location}
              </Typography>
            )}

            <CommunicationActionsContainer>
              <EmailAction href={`mailto:${entity?.email}`} hasEmail={!!entity?.email}>
                <FPIconButton
                  tooltipProps={{ title: strings.shared.rowActions.actions.email }}
                  disabled={!entity?.email}
                  aria-label="send email"
                >
                  <FPIcon
                    component={SvgEmailFilled}
                    color={entity?.email ? ICON_COLOR : DISABLED_COLOR}
                  />
                </FPIconButton>
              </EmailAction>
              <PhoneNumbersMenu
                contact={contactData}
                onPhoneClick={handlePhoneClick}
                onTextClick={handleOnTextClick}
                icon="phone"
                tooltipTitle={strings.dailyPlan.drawer.taskDetails.callText}
              />
            </CommunicationActionsContainer>
          </ReachOutToContainer>
        </When>

        {status && (
          <Typography color="textSecondary" variant="body1" className="align-items-center">
            {Icon && <Icon className={`task-status-icon task-status-${status?.id}`} />}&nbsp;
            {status?.title}
          </Typography>
        )}
      </Content>
    </Grid>
  );
};
